import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter } from "react-router-dom";

import App from "../App.js";

import Error from "../Components/CommonComponents/Error/index.js";
import ContactUsPageSkeleton from "../Components/CommonComponents/SkeletonUI/ContactUsSkeleton.js";
import LandingPageSkeleton from "../Components/CommonComponents/SkeletonUI/LandingPageSkeleton.js";
import OfferSummaryPageSkeleton from "../Components/CommonComponents/SkeletonUI/OrderSummarypageSkeleton.js";
import RewardDetailSkeleton from "../Components/CommonComponents/SkeletonUI/RewardDetailSkeleton.js";
import RewardListingSkeleton from "../Components/CommonComponents/SkeletonUI/RewardListingSkeleton.js";
import Spinner from "../Components/CommonComponents/Spinner/index.js";

import PrivateRoute from "./PrivateRoute.js";

import { getSubdomainInfo, MainDomainRedirect } from "../Utils/CommonFunctions.js";

import ErrorBoundary from "../hoc/ErrorBoundary.js";

import { ACTIVE_SUBSCRIPTION_DETAILPAGE, ASSIGNED_OFFER_PAGE, COMMERCE_DETAIL, COMMERCE_MODULE, CONTACT_US_PAGE, DISCLAIMER_PAGE, EXPERIENCE_DETAIL, EXPERIENCE_MODULE, FAQ_PAGE, HOMEPAGE, INACTIVE_SUBSCRIPTION_DETAILPAGE, POLICY_PAGE, POLICY_PRIVACY_PAGE, PUBIC_REWARD_DETAIL_PAGE, PUBIC_REWARD_LISTING, PUBLIC_LISTING_PAGE, REFUND_PAGE, REWARD_DETAIL_CASHBACK_PAGE, REWARD_DETAIL_DISCOUNTOFFER_PAGE, REWARD_DETAIL_GIFTCARD_PAGE, REWARD_DETAIL_MEMBERSHIP_PAGE, REWARD_LISTING, REWARD_SUMMARY_PAGE, SUBSCRIPTION_LISTING, TERMS_PAGE, TRAVEL_BUS_MODULE, TRAVEL_FLIGHT_MODULE, TRAVEL_HOTEL_MODULE, TRAVEL_MODULE, TRAVEL_TRAIN_MODULE, USER_NOTIFICATON_PAGE, USER_ORDER_PAGE, USER_PROFILE_PAGE, USER_REWARD_PAGE, USER_SUBSCRIPTION_PAGE, USER_WISHLIST_PAGE, VIP_DETAIL, VIP_MODULE } from "./RoutesContant.js";

import { useAuth } from "../hoc/AuthContext.js";

// Lazy load the components

// main homepage and landing routing
const LandingPage = lazy(() => import("../Components/LandingPage/index.js"));
const HomePage = lazy(() => import("../Components/HomePage/index.js"));

// Rewards module routing
const RewardPage = lazy(() => import("../Components/Pages/Reward/index.js"));
const RewardDetailGiftCardPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/GiftCardRewardDetailPage.js"));
const RewardDetailCashbackPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/CashbackRewardDetailPage.js"));
const RewardDetailMembershipPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/AccessMembershipRewardDetailPage.js"));
// const RewardDetailExperiencePage = lazy(() => import("../Componentss/RewardDetailPage/ExperienceRewardDetailPage.js"));
const RewardDetailDiscountPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/DiscountRewardDetailPage.js"));
const OfferSummaryPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/OfferSummary.js"));

// User profile routing
const UserProfliePage = lazy(() => import("../Components/UserProfile/index.js"));
const AssignedOffer = lazy(() => import("../Components/UserProfile/AssignedOffer.js"));
const OrderPage = lazy(() => import("../Components/UserProfile/OrderHistory.js"));
const RewardUserPage = lazy(() => import("../Components/UserProfile/Rewardtab.js"));
const WishlistPage = lazy(() => import("../Components/UserProfile/WishlistTab.js"));
const NotificationPage = lazy(() => import("../Components/UserProfile/NotificationSetting.js"));
const ProfilePage = lazy(() => import("../Components/UserProfile/Profiletab.js"));
const UserSubscriptionPage = lazy(() => import("../Components/UserProfile/SubscriptionPage.js"));

// contact and privacy policies pages
const ContactUsPage = lazy(() => import("../Components/LandingPage/ContactUsPage.js"));
const PolicyPage = lazy(() => import("../Components/LandingPage/PrivacyPolicyPage/index.js"));
const PrivacyPolicy = lazy(() => import("../Components/LandingPage/PrivacyPolicy.js"));
const Faq = lazy(() => import("../Components/LandingPage/Faq.js"));
const Disclaimer = lazy(() => import("../Components/LandingPage/Disclaimer.js"));
const Refund = lazy(() => import("../Components/LandingPage/Refund.js"));
const TermsOfUse = lazy(() => import("../Components/LandingPage/TermOfUse.js"));

// subscription routing 
const Subscription = lazy(() => import("../Components/Pages/Subscription/index.js"));
const ActiveSubscriptionDetail = lazy(() => import("../Components/Pages/Subscription/SubscriptionDetail.js"));
const InactiveSubscriptionDetail = lazy(() => import("../Components/Pages/Subscription/SubscriptionDetail.js"));

// Travel routing
const TravelPage = lazy(() => import("../Components/Pages/Travel/index.js"));
// const FlightPage = lazy(() => import("../Components/Pages/Travel/index.js"));
// const HotelPage = lazy(() => import("../Components/Pages/Travel/index.js"));
// const TrainPage = lazy(() => import("../Components/Pages/Travel/index.js"));
// const BusPage = lazy(() => import("../Components/Pages/Travel/index.js"));

// const TrainDetailPage = lazy(() => import("../Components/Pages/Travel/Train/TrainDetail.js"));
// const FlightDetailPage = lazy(() => import("../Components/Pages/Travel/Flight/FlightDetail/FlightDetailWeb"));
// const HotelDetailPage = lazy(() => import("../Components/Pages/Travel/Hotel/HotelDetail.js"));
// const BusDetailPage = lazy(() => import("../Components/Pages/Travel/Bus/BusDetail.js"));

// Commerce routing
const CommercePage = lazy(() => import("../Components/Pages/Commerce/index.js"));
const CommerceDetailPage = lazy(() => import("../Components/Pages/Commerce/CommerceDetail.js"));

// experience routing
const ExperiencePage = lazy(() => import("../Components/Pages/Experience/index.js"));
const ExperienceDetailPage = lazy(() => import("../Components/Pages/Experience/ExperienceDetail.js"));

// VIP routing
const VIPPage = lazy(() => import("../Components/Pages/VIP/index.js"));
const VIPDetailPage = lazy(() => import("../Components/Pages/VIP/VIPDetail.js"));

// Subdomain info
const { isSubdomain, subdomainName } = getSubdomainInfo();
const isMainDomain = !isSubdomain;
const windowURLSearchParam = window?.location?.search;
const windowLocationUrl = window?.location?.href;
const urlParams = new URLSearchParams(windowURLSearchParam);
const keyParam = urlParams.get('key');

const AppWithState = () => {
  const { setWindowLocationUrl, setShowSignUp, isAuthenticated } = useAuth();
  useEffect(() => {
    if (keyParam) {
      // Store the card number in localStorage
      localStorage.setItem("card_number", keyParam);

      // Only show signup if user is not authenticated 
      if (!isAuthenticated) {
        setWindowLocationUrl(windowLocationUrl);
        setShowSignUp(true);
      }
    }
    // Set windowLocationUrl when the component mounts
    setWindowLocationUrl(windowLocationUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <App />
  );
};

// Error boundary wrapper component
const WrapWithErrorBoundary = ({ children, skeleton }) => (
  <ErrorBoundary fallback={<Error />}>
    <Suspense fallback={skeleton || <Spinner />}>{children}</Suspense>
  </ErrorBoundary>
);

export const routes = createBrowserRouter([
  {
    path: '/',
    element: <>
      <MainDomainRedirect />
      <AppWithState />
    </>,
    errorElement: <Error />,
    children: [
      {
        path: HOMEPAGE,
        element:
          <WrapWithErrorBoundary skeleton={<LandingPageSkeleton />}>
            {isMainDomain ? <LandingPage /> : <HomePage subdomainName={subdomainName} />}
          </WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: CONTACT_US_PAGE,
        element: <WrapWithErrorBoundary skeleton={<ContactUsPageSkeleton />}><ContactUsPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: PUBIC_REWARD_LISTING,
        element: (
          <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
            <RewardPage windowURLSearchParam={windowURLSearchParam} isPublic={true} />
          </WrapWithErrorBoundary>
        ),
        errorElement: <Error />,
      },
      // {
      //   path: PUBLIC_LISTING_PAGE,
      //   element: (
      //     <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
      //       <RewardPage windowURLSearchParam={windowURLSearchParam} isPublic={true} />
      //     </WrapWithErrorBoundary>
      //   ),
      //   errorElement: <Error />,
      // },
      {
        path: PUBIC_REWARD_DETAIL_PAGE,
        element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailDiscountPage subdomainName={subdomainName} isSubdomain={isSubdomain} isPublic={true} /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: POLICY_PAGE,
        element: <WrapWithErrorBoundary><PolicyPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
        children: [
          {
            path: POLICY_PRIVACY_PAGE,
            element: <WrapWithErrorBoundary><PrivacyPolicy /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: TERMS_PAGE,
            element: <WrapWithErrorBoundary><TermsOfUse /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: FAQ_PAGE,
            element: <WrapWithErrorBoundary><Faq /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: DISCLAIMER_PAGE,
            element: <WrapWithErrorBoundary><Disclaimer /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REFUND_PAGE,
            element: <WrapWithErrorBoundary><Refund /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
        ],
      },
      // Travel Module routing
      {
        path: TRAVEL_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_FLIGHT_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_BUS_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_HOTEL_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_TRAIN_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: "/",
        element: <PrivateRoute />,
        errorElement: <Error />,
        children: [
          //  Subscription Module Routing
          {
            path: SUBSCRIPTION_LISTING,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <Subscription windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: ACTIVE_SUBSCRIPTION_DETAILPAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><ActiveSubscriptionDetail active={true} /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: INACTIVE_SUBSCRIPTION_DETAILPAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><InactiveSubscriptionDetail active={false} /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  VIP Module routing
          {
            path: VIP_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <VIPPage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: VIP_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><VIPDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Experience Module routing
          {
            path: EXPERIENCE_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <ExperiencePage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: EXPERIENCE_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><ExperienceDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Commerce Module routing
          {
            path: COMMERCE_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <CommercePage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: COMMERCE_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><CommerceDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Rewards Module routing
          {
            path: REWARD_LISTING,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <RewardPage windowURLSearchParam={windowURLSearchParam} isPublic={false} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_GIFTCARD_PAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailGiftCardPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_DISCOUNTOFFER_PAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailDiscountPage isPublic={false} /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          // {
          //   path: REWARD_DETAIL_EXPERIENCES_PAGE,
          //   element: <WrapWithErrorBoundary><RewardDetailExperiencePage /></WrapWithErrorBoundary>,
          //   errorElement: <Error />,
          // },
          {
            path: REWARD_DETAIL_MEMBERSHIP_PAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailMembershipPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_CASHBACK_PAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailCashbackPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_SUMMARY_PAGE,
            element: <WrapWithErrorBoundary skeleton={<OfferSummaryPageSkeleton />}><OfferSummaryPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          // User Profile routing
          {
            path: USER_PROFILE_PAGE,
            element: <WrapWithErrorBoundary><UserProfliePage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
            children: [
              {
                path: "",
                element: <WrapWithErrorBoundary><ProfilePage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: USER_ORDER_PAGE,
                element: <WrapWithErrorBoundary><OrderPage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: USER_WISHLIST_PAGE,
                element: <WrapWithErrorBoundary><WishlistPage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: USER_REWARD_PAGE,
                element: <WrapWithErrorBoundary><RewardUserPage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: USER_NOTIFICATON_PAGE,
                element: <WrapWithErrorBoundary><NotificationPage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: ASSIGNED_OFFER_PAGE,
                element: <WrapWithErrorBoundary><AssignedOffer /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
              {
                path: USER_SUBSCRIPTION_PAGE,
                element: <WrapWithErrorBoundary><UserSubscriptionPage /></WrapWithErrorBoundary>,
                errorElement: <Error />,
              },
            ],
          },
        ],
      },
    ],
  },
]);
